import { useEffect, useMemo, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useSite } from "@/context/SiteContext";

const COLORS = ["#ff4d8d", "#ffd23f", "#38b6ff", "#2fb344", "#7c4dff", "#ff7a2f"];
const BEAMS = [
  { left: "8%", color: "#ff4d8d", delay: "0s" },
  { left: "32%", color: "#38b6ff", delay: "-1.2s" },
  { left: "58%", color: "#ffd23f", delay: "-0.6s" },
  { left: "84%", color: "#2fb344", delay: "-1.8s" },
];

const Confetti = () => {
  const pieces = useMemo(
    () => Array.from({ length: 70 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: -Math.random() * 4,
      dur: 3 + Math.random() * 3,
      size: 7 + Math.random() * 8,
      color: COLORS[i % COLORS.length],
      round: i % 3 === 0,
      drift: (Math.random() - 0.5) * 160,
    })),
    []
  );
  return pieces.map((p) => (
    <span
      key={p.id}
      className="confetti-piece absolute top-0"
      style={{
        left: `${p.left}%`,
        width: p.size,
        height: p.round ? p.size : p.size * 0.45,
        background: p.color,
        borderRadius: p.round ? "50%" : 2,
        animationDuration: `${p.dur}s`,
        animationDelay: `${p.delay}s`,
        "--drift": `${p.drift}px`,
      }}
    />
  ));
};

const Flash = () => {
  const ref = useRef(null);
  const n = useRef(0);
  useEffect(() => {
    const onBeat = () => {
      const el = ref.current;
      if (!el) return;
      n.current += 1;
      el.style.background = `radial-gradient(circle at 50% 40%, ${COLORS[n.current % COLORS.length]}55, transparent 65%)`;
      el.animate([{ opacity: 0.9 }, { opacity: 0 }], { duration: 380, easing: "ease-out" });
    };
    window.addEventListener("ananawi-beat", onBeat);
    return () => window.removeEventListener("ananawi-beat", onBeat);
  }, []);
  return <div ref={ref} className="absolute inset-0 opacity-0" />;
};

export const DanceFloor = () => {
  const { party } = useSite();
  return (
    <AnimatePresence>
      {party && (
        <motion.div
          key="dance-floor"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-[55] pointer-events-none overflow-hidden"
          data-testid="dance-floor"
        >
          {BEAMS.map((b) => (
            <span
              key={b.left}
              className="dance-beam absolute -top-[10%] h-[130%] w-[22vw] origin-top"
              style={{ left: b.left, background: `linear-gradient(to bottom, ${b.color}66, transparent 75%)`, animationDelay: b.delay }}
            />
          ))}
          <Flash />
          <Confetti />
        </motion.div>
      )}
    </AnimatePresence>
  );
};
